import React, { useState, useEffect, useContext } from "react";
import { useSearchParamsState } from "../../hooks/useSearchParamsState";
import Grid from "../Grid";
import styled, { css } from "styled-components";
import ResetIcon from "../Icons/Reset";
import { FilterDropdown } from "./Dropdown";
import { ProductList } from "../ProductList";
import { Toggle } from "./Toggle";
import CloseV2 from "../Icons/CloseV2";
import Section from "../Section";
import Container from "../Container";
import { useProductCategory } from "../../context/ProductCategoryContext";
import { Spinner } from "../Icons/Spinner";
import { useFilterGlobals } from "../../hooks/useFilterGlobals";
import FilterIcon from "../Icons/Filter";

const FilterStyles = styled.form`
  ${({ ...props }) => css`
    background-color: rgb(8 8 8 / 84%);
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 999999999999999;
    overflow: auto;
    display: flex;
    align-items: center;

    @media (min-width: ${props.theme.breakpoints.md}) {
      padding-top: 100px;
      padding-bottom: 100px;
      z-index: 100;
    }
  `}
`;
const FilterContainerStyles = styled.div`
  ${({ ...props }) => css`
    background-color: ${props.theme.colors.lightGrey};
    overflow: hidden;
    border-radius: 4px;
    max-width: 1204px;
    width: 100%;
    max-height: 100%;
    overflow: auto;
    margin: 0 auto;
  `}
`;
const FilterHeaderStyles = styled.div`
  ${({ ...props }) => css`
    background-color: ${props.theme.colors.primary};
    color: #fff;
    padding: 32px;
    position: relative;

    h1 {
      display: flex;
      gap: 20px;
      align-items: center;
      font-size: 1.875rem;
      line-height: 1.2;

      svg {
        width: 2.25rem;
        height: auto;
      }
    }

    @media (min-width: ${props.theme.breakpoints.md}) {
      padding: 54px;
      padding-right: 70px;

      h1 {
        font-size: 2.5rem;
        gap: 30px;
      }

      svg {
        width: 3rem;
      }
    }
  `}
`;
const FilterCloseButtonStyles = styled.button`
  ${({ ...props }) => css`
    position: absolute;
    right: 24px;
    top: 24px;
    display: flex;
    align-items: center;
    gap: 8px;

    @media (max-width: 767px) {
      padding: 0;

      span {
        display: none;
      }
    }
  `}
`;
const FilterBodyStyles = styled.div`
  ${({ ...props }) => css`
    padding-left: 32px;
    padding-right: 32px;
    padding-top: 24px;
    padding-bottom: 42px;
    position: relative;

    .toggle-container {
      margin-bottom: 24px;
    }

    .toggle-container > div {
      margin: 0;
    }

    .filter-container {
      margin-left: -8px;
      margin-right: -8px;
    }

    .filter-dropdown {
      padding-left: 8px;
      padding-right: 8px;
    }

    @media (min-width: ${props.theme.breakpoints.md}) {
      padding-left: 42px;
      padding-right: 42px;
      padding-top: 50px;
      padding-bottom: 82px;

      .toggle-container > div {
        width: fit-content;
        flex: initial;
      }
    }
  `}
`;

const FilterBannerStyles = styled.div`
  ${({ ...props }) => css`
    h2, h3 {
      font-size: 1.875rem;
      line-height: 1.2;
    }
    h2 {
      color: ${props.theme.colors.primary};
    }
    h3 {
      color: ${props.theme.colors.darkGrey};
    }
    
    @media (min-width: ${props.theme.breakpoints.md}) {
      .grid-container {
        flex-flow: row;
        justify-content: center;
        column-gap: 90px;
      }
      .grid-item {
        width: fit-content;
        flex: initial;
      }
    }
  `}
`;

const UseFiltersButtonStyles = styled.div`
${({ ...props }) => css`
  display: flex;
  align-items: center;

  button {
    background-color: ${props.theme.colors.darkGrey};
    display: flex;
    align-items: center;
    padding: 12px 32px 12px 22px;
    gap: 22px;
    font-size: 1rem;
    line-height: 1;

    &:hover {
      background-color: ${props.theme.colors.darkGreyHover};
    }

    svg {
      width: 2rem;
      height: auto;
    }
  }
`}
`;

const ResetButtonStyles = styled.button`
  ${({ ...props }) => css`
    display: inline-flex;
    align-items: center;
    background: transparent;
    color: ${props.theme.colors.darkGrey};
    padding: 0;
    margin-left: 16px;
    text-transform: none;
    font-weight: 600;
    font-size: 0.875rem;

    &:hover, &:focus {
      background: transparent;
      color: ${props.theme.colors.darkGreyHover};
    }
    
    svg {
      margin-right: 16px;
      transform: rotate(315deg) scaleX(-1)
    }
  `}
`;

const SpinnerOverlayStyles = styled.div`
  ${({ ...props }) => css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.8);
    z-index: 10;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
      width: 100px;
      height: 100px;
    }

    @media (min-width: ${props.theme.breakpoints.md}) {
      svg {
        width: 145px;
        height: 145px;
      }
    }
  `}
`;

const FilterResultsStyles = styled.div`
  ${({ ...props }) => css`
    margin-top: 44px;

    > h3 {
      font-size: 1.375rem;
      margin-bottom: 22px;
      line-height: 1.2;
    }

    @media (min-width: ${props.theme.breakpoints.md}) {
      > h3 {
        font-size: 1.875rem;
      }
    }
  `}
`;

/**
 * ToDo
 *  Implement Chapter 8 Filter
 */
export const Filters = ({
  title,
  subtitle,
  showApplicationsFilter,
  showDepthFilter,
  showFilledWeightFilter,
  showHeightFilter,
  showHeightWithFenceFilter,
  showWeightFilter,
  showWidthFilter,
  showCrashTestedFilter,
  showWindTunnelTestedFilter,
  showChapter8Filter,
  crashTestedTooltip,
  windTunnelTestedTooltip,
  chapter8Tooltip,
}) => {
  // Disabled all filters, so don't render this component
  if (
    !showApplicationsFilter &&
    !showDepthFilter &&
    !showFilledWeightFilter &&
    !showHeightFilter &&
    !showHeightWithFenceFilter &&
    !showWeightFilter &&
    !showWidthFilter &&
    !showCrashTestedFilter &&
    !showWindTunnelTestedFilter &&
    !showChapter8Filter
  ) {
    return null
  }

  /**
   * State
   */
  const filterGlobals = useFilterGlobals();
  const { products: allProducts } = useProductCategory();
  const [filteredProducts, setFilteredProducts] = useState([]);
  // State of selected filters
  const defaultFilter = "All";
  const [selectedWidthFilter, setSelectedWidthFilter] = useSearchParamsState("width", defaultFilter);
  const [selectedFilledWeightFilter, setSelectedFilledWeightFilter] = useSearchParamsState("filledWeight", defaultFilter);
  const [selectedHeightFilter, setSelectedHeightFilter] = useSearchParamsState("height", defaultFilter);
  const [selectedHeightWithFenceFilter, setSelectedHeightWithFenceFilter] = useSearchParamsState("heightWithFencing", defaultFilter);
  const [selectedDepthFilter, setSelectedDepthFilter] = useSearchParamsState("depth", defaultFilter);
  const [selectedWeightFilter, setSelectedWeightFilter] = useSearchParamsState("weight", defaultFilter);
  const [selectedApplicationFilter, setSelectedApplicationFilter] = useSearchParamsState("application", defaultFilter);
  const [selectedCrashTestedFilter, setSelectedCrashTestedFilter] = useSearchParamsState("crashTested", defaultFilter);
  const [selectedWindTunnelFilter, setSelectedWindTunnelFilter] = useSearchParamsState("windTunnel", defaultFilter);
  // Set inital filter options states
  const [widthFilters, setWidthFilters] = useState([]);
  const [heightFilters, setHeightFilters] = useState([]);
  const [heightWithFenceFilters, setHeightWithFenceFilters] = useState([]);
  const [depthFilters, setDepthFilters] = useState([]);
  const [weightFilters, setWeightFilters] = useState([]);
  const [filledWeightFilters, setFilledWeightFilters] = useState([]);
  const [applicationFilters, setApplicationFilters] = useState([]);
  // Filter modal state
  const [filtersModalActive, setFiltersModalActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Count state
  const [crashTestCount, setCrashTestCount] = useState(0);
  const [windTunnelCount, setWindTunnelCount] = useState(0);
  // Whether to show results
  const filtersAreActive = (
    selectedWidthFilter !== defaultFilter ||
    selectedHeightFilter !== defaultFilter ||
    selectedHeightWithFenceFilter !== defaultFilter ||
    selectedDepthFilter !== defaultFilter ||
    selectedWeightFilter !== defaultFilter ||
    selectedFilledWeightFilter !== defaultFilter ||
    selectedApplicationFilter!= defaultFilter ||
    selectedCrashTestedFilter !== defaultFilter ||
    selectedWindTunnelFilter !== defaultFilter
  ) && !isLoading;

  /**
   * Functions
   */
  function matchingProducts(filteredProducts, selectedValue, filterProperty) {
    return filteredProducts.filter((product) => {
      const filterRange = product[filterProperty];
      if (filterRange && filterRange.nodes.length > 0) {
        return filterRange.nodes.some((range) => range.name === selectedValue);
      }
    });
  }
  function filterProducts() {
    if (!allProducts || allProducts.length === 0) {
      return;
    }

    setFilteredProducts([])
    let result = allProducts;
  
    if (selectedWidthFilter !== defaultFilter) {
      result = matchingProducts(result, selectedWidthFilter, 'widthRanges');
    }
    if (selectedHeightFilter !== defaultFilter) {
      result = matchingProducts(result, selectedHeightFilter, 'heightRanges');
    }
    if (selectedHeightWithFenceFilter !== defaultFilter) {
      result = matchingProducts(result, selectedHeightWithFenceFilter, 'heightWithFenceRanges');
    }
    if (selectedDepthFilter !== defaultFilter) {
      result = matchingProducts(result, selectedDepthFilter, 'depthRanges');
    }
    if (selectedWeightFilter !== defaultFilter) {
      result = matchingProducts(result, selectedWeightFilter, 'weightRanges');
    }
    if (selectedFilledWeightFilter !== defaultFilter) {
      result = matchingProducts(result, selectedFilledWeightFilter, 'filledWeightRanges');
    }
    if (selectedApplicationFilter !== defaultFilter) {
      result = result.filter(product =>
        product.applications?.nodes?.some(app => app.name === selectedApplicationFilter)
      );
    }
    if (selectedCrashTestedFilter === "true") {
      result = result.filter(product =>
        !!product?.productData?.specification?.crashtested === true
      );
    }
    if (selectedWindTunnelFilter === "true") {
      result = result.filter(product =>
        !!product?.productData?.specification?.windtunneltested === true
      );
    }
  
    setFilteredProducts(result);
  }

  /**
   * Effects
   */
  // Update filtered products when filters change
  useEffect(() => {
    const timeout = setTimeout(() => {
      filterProducts();
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [
    allProducts,
    selectedWidthFilter,
    selectedHeightFilter,
    selectedHeightWithFenceFilter,
    selectedDepthFilter,
    selectedWeightFilter,
    selectedFilledWeightFilter,
    selectedApplicationFilter,
    selectedCrashTestedFilter,
    selectedWindTunnelFilter,
  ]);
  useEffect(() => {
    const createFilterList = (rangeKey) => {
      const map = new Map();
      filteredProducts.forEach(p => {
        p[rangeKey]?.nodes?.forEach(n => {
          if (!map.has(n.name)) map.set(n.name, 0);
          map.set(n.name, map.get(n.name) + 1);
        });
      });
      
      return [...map.entries()].map(([name, count]) => {
        // Find a matching term object from any product
        const matchingTerm = filteredProducts
          .flatMap(p => p[rangeKey]?.nodes || [])
          .find(n => n.name === name);
      
        return {
          name,
          count,
          termOrder: matchingTerm?.termOrder ?? null,
        };
      });
    };
  
    setWidthFilters(createFilterList('widthRanges'));
    setHeightFilters(createFilterList('heightRanges'));
    setHeightWithFenceFilters(createFilterList('heightWithFenceRanges'));
    setDepthFilters(createFilterList('depthRanges'));
    setWeightFilters(createFilterList('weightRanges'));
    setFilledWeightFilters(createFilterList('filledWeightRanges'));
  
    const appMap = new Map();
    filteredProducts.forEach(p => {
      p.applications?.nodes?.forEach(app => {
        if (!appMap.has(app.name)) appMap.set(app.name, 0);
        appMap.set(app.name, appMap.get(app.name) + 1);
      });
    });
      
    setApplicationFilters([...appMap.entries()].map(([name, count]) => {
      // Find a matching term object from any product
        const matchingTerm = filteredProducts
          .flatMap(p => p.applications?.nodes || [])
          .find(n => n.name === name);
      
        return {
          name,
          count,
          termOrder: matchingTerm?.termOrder ?? null,
        };
    })
  );
  }, [filteredProducts]);
  useEffect(() => {
    if (!filteredProducts || filteredProducts.length === 0) return;
  
    const crashCount = filteredProducts.filter(
      p => !!p?.productData?.specification?.crashtested === true
    ).length;
  
    const windCount = filteredProducts.filter(
      p => !!p?.productData?.specification?.windtunneltested === true
    ).length;
  
    setCrashTestCount(crashCount);
    setWindTunnelCount(windCount);
  }, [filteredProducts]);

  /**
   * Actions
   */
  function handleResetFilters() {
    setSelectedApplicationFilter(defaultFilter)
    setSelectedCrashTestedFilter(defaultFilter)
    setSelectedDepthFilter(defaultFilter)
    setSelectedFilledWeightFilter(defaultFilter)
    setSelectedHeightFilter(defaultFilter)
    setSelectedHeightWithFenceFilter(defaultFilter)
    setSelectedWidthFilter(defaultFilter)
    setSelectedWeightFilter(defaultFilter)
    setSelectedWindTunnelFilter(defaultFilter)
  }
  const handleBackgroundClick = (e) => {
    if (e.target.id === 'filter-background') {
      setFiltersModalActive(false);
    }
  };

  return (
    <>
      <FilterBannerStyles>
        <Section lightBlue>
          <Container>
            <Grid equalHeight className="grid-container">
              <Grid item className="grid-item">
                <div>
                  <h2>
                    {!!title ? title : filterGlobals.filterTitle}
                  </h2>
                  <h3>
                    {!!subtitle ? subtitle : filterGlobals.filterSubtitle}
                  </h3>
                </div>
              </Grid>
              <Grid item className="grid-item">
                <UseFiltersButtonStyles>
                  <button
                    type="button"
                    onClick={() => setFiltersModalActive(true)}
                  >
                    <FilterIcon />
                    <span>Use Filters</span>
                  </button>
                </UseFiltersButtonStyles>
              </Grid>
            </Grid>
          </Container>
        </Section>
      </FilterBannerStyles>
      {filtersModalActive && (
        <FilterStyles
          onClick={(e) => handleBackgroundClick(e)}
          id="filter-background"
        >
          <FilterContainerStyles>
            <FilterHeaderStyles>
              <h1>
                <FilterIcon />
                <strong>Filter Products</strong>
              </h1>
              <FilterCloseButtonStyles onClick={() => {
                setFiltersModalActive(false)
              }}>
                <CloseV2 />
                <span>Close</span>
              </FilterCloseButtonStyles>
            </FilterHeaderStyles>
            <FilterBodyStyles>
              <Grid equalHeight className="toggle-container">
                <Grid item>
                  <Toggle
                    label="Crash Tested"
                    name="crashTested-filters"
                    value={selectedCrashTestedFilter}
                    defaultValue={defaultFilter}
                    onChange={(e) => {
                      setIsLoading(true)
                      setSelectedCrashTestedFilter(e?.target?.value)
                    }}
                    show={showCrashTestedFilter}
                    disabled={selectedCrashTestedFilter === defaultFilter && crashTestCount === 0}
                    tooltip={!!crashTestedTooltip ? crashTestedTooltip : filterGlobals.filterCrashTestedTooltip}
                  />
                </Grid>
                <Grid item>
                  <Toggle
                    label="Wind Tested"
                    name="windTunnelTested-filters"
                    value={selectedWindTunnelFilter}
                    defaultValue={defaultFilter}
                    onChange={(e) => {
                      setIsLoading(true)
                      setSelectedWindTunnelFilter(e?.target?.value)
                    }}
                    show={showWindTunnelTestedFilter}
                    disabled={selectedWindTunnelFilter === defaultFilter && windTunnelCount === 0}
                    tooltip={!!windTunnelTestedTooltip ? windTunnelTestedTooltip : filterGlobals.filterWindTunnelTestedTooltip}
                  />
                </Grid>
                <Grid item>
                  <Toggle
                    label="Chapter 8"
                    name="chapter8-filters"
                    // value={selectedWindTunnelFilter}
                    defaultValue={defaultFilter}
                    // onChange={(e) => {
                    //   setIsLoading(true)
                    //   setSelectedWindTunnelFilter(e?.target?.value)
                    // }}
                    show={showChapter8Filter}
                    // disabled={selectedWindTunnelFilter === defaultFilter && windTunnelCount === 0}
                    tooltip={!!chapter8Tooltip ? chapter8Tooltip : filterGlobals.filterChapter8Tooltip}
                  />
                </Grid>
              </Grid>
              <Grid equalHeight rowGap="16px" className="filter-container">
                <FilterDropdown
                  label="Application"
                  name="application-filters"
                  filters={applicationFilters}
                  selectedValue={selectedApplicationFilter}
                  onChange={(e) => {
                    setIsLoading(true)
                    setSelectedApplicationFilter(e?.target?.value)
                  }}
                  defaultValue={defaultFilter}
                  show={showApplicationsFilter}
                />

                <FilterDropdown
                  label="Height"
                  name="height-filters"
                  filters={heightFilters}
                  selectedValue={selectedHeightFilter}
                  onChange={(e) => {
                    setIsLoading(true)
                    setSelectedHeightFilter(e?.target?.value)
                  }}
                  defaultValue={defaultFilter}
                  show={showHeightFilter}
                />

                <FilterDropdown
                  label="Width"
                  name="width-filters"
                  filters={widthFilters}
                  selectedValue={selectedWidthFilter}
                  onChange={(e) => {
                    setIsLoading(true)
                    setSelectedWidthFilter(e?.target?.value)
                  }}
                  defaultValue={defaultFilter}
                  show={showWidthFilter}
                />

                <FilterDropdown
                  label="Depth"
                  name="depth-filters"
                  filters={depthFilters}
                  selectedValue={selectedDepthFilter}
                  onChange={(e) => {
                    setIsLoading(true)
                    setSelectedDepthFilter(e?.target?.value)
                  }}
                  defaultValue={defaultFilter}
                  show={showDepthFilter}
                />

                <FilterDropdown
                  label="Weight"
                  name="weight-filters"
                  filters={weightFilters}
                  selectedValue={selectedWeightFilter}
                  onChange={(e) => {
                    setIsLoading(true)
                    setSelectedWeightFilter(e?.target?.value)
                  }}
                  defaultValue={defaultFilter}
                  show={showWeightFilter}
                />

                <FilterDropdown
                  label="Filled Weight"
                  name="filled-weight-filters"
                  filters={filledWeightFilters}
                  selectedValue={selectedFilledWeightFilter}
                  onChange={(e) => {
                    setIsLoading(true)
                    setSelectedFilledWeightFilter(e?.target?.value)
                  }}
                  defaultValue={defaultFilter}
                  show={showFilledWeightFilter}
                />

                <FilterDropdown
                  label="Height with Fence"
                  name="height-with-fence-filters"
                  filters={heightWithFenceFilters}
                  selectedValue={selectedHeightWithFenceFilter}
                  onChange={(e) => {
                    setIsLoading(true)
                    setSelectedHeightWithFenceFilter(e?.target?.value)
                  }}
                  defaultValue={defaultFilter}
                  show={showHeightWithFenceFilter}
                />

                <Grid item xs={6} md={4} lg={3}>
                  <ResetButtonStyles type="button" onClick={handleResetFilters}>
                    <ResetIcon /> Clear filters
                  </ResetButtonStyles>
                </Grid>
              </Grid>
              {filtersAreActive && (
                <FilterResultsStyles>
                  <h3>{filteredProducts.length} Results</h3>
                  <ProductList
                    products={filteredProducts}
                    addToContext={false}
                    grey
                    inner
                    sort="price"
                  />
                </FilterResultsStyles>
              )}
              {isLoading && (
                <SpinnerOverlayStyles>
                  <Spinner />
                </SpinnerOverlayStyles>
              )}
            </FilterBodyStyles>
          </FilterContainerStyles>
        </FilterStyles>
      )}
    </>
  );
};
